import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import durationPlugin from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/en';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(dayOfYear);
dayjs.extend(durationPlugin);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(minMax);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(relativeTime);
dayjs.locale('en');

/** en-US weekday names (Sunday–Saturday), matching former moment.weekdays(). */
export const WEEKDAY_NAMES = dayjs.weekdays();

const WEEKDAY_INDEX_BY_NAME = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/**
 * @param {string|number} nameOrNumber
 * @returns {number}
 */
export function weekdayIndex(nameOrNumber) {
  if (typeof nameOrNumber === 'number') {
    return nameOrNumber;
  }

  const index = WEEKDAY_INDEX_BY_NAME[String(nameOrNumber).toLowerCase()];

  if (index === undefined) {
    throw new Error(`Invalid weekday: ${nameOrNumber}`);
  }

  return index;
}

/**
 * @param {import('dayjs').ConfigType} [value]
 * @param {import('dayjs').OpUnitType} [unit]
 * @returns {import('dayjs').Duration}
 */
export function duration(value, unit) {
  if (value === undefined || value === null) {
    return dayjs.duration(0);
  }

  if (unit) {
    return dayjs.duration(value, unit);
  }

  return dayjs.duration(value);
}

/**
 * @param {*} value
 * @returns {value is import('dayjs').Dayjs}
 */
export function isDate(value) {
  return dayjs.isDayjs(value);
}

/**
 * @param {...import('dayjs').Dayjs} dates
 * @returns {import('dayjs').Dayjs}
 */
export function max(...dates) {
  return dayjs.max(...dates);
}

/**
 * @param {...import('dayjs').Dayjs} dates
 * @returns {import('dayjs').Dayjs}
 */
export function min(...dates) {
  return dayjs.min(...dates);
}

const ISO8601_OFFSET_SUFFIX = /([+-]\d{2}:\d{2}|Z)$/i;

/**
 * @param {import('dayjs').ConfigType} value
 * @returns {number|null}
 */
function parseIsoOffsetMinutes(value) {
  const match = String(value).match(ISO8601_OFFSET_SUFFIX);

  if (!match) {
    return null;
  }

  const suffix = match[1];

  if (suffix.toUpperCase() === 'Z') {
    return 0;
  }

  const sign = suffix.startsWith('-') ? -1 : 1;
  const [hours, minutes] = suffix.slice(1).split(':');

  return sign * ((parseInt(hours, 10) * 60) + parseInt(minutes, 10));
}

/**
 * Parses a date string preserving offset from the input (moment.parseZone equivalent).
 *
 * Exported as a named binding for stale cached modules that still import `{ parseZone }`.
 * New code should call `date.parseZone()` on the default export instead.
 *
 * @param {import('dayjs').ConfigType} input
 * @returns {import('dayjs').Dayjs}
 */
export function parseZone(input) {
  const value = String(input);
  const parsed = dayjs(value);

  if (!parsed.isValid()) {
    return parsed;
  }

  const offsetMinutes = parseIsoOffsetMinutes(value);

  if (offsetMinutes === null) {
    return parsed;
  }

  const wallClock = value.slice(0, 19);

  return dayjs(wallClock).utcOffset(offsetMinutes, true);
}

dayjs.parseZone = parseZone;

export default dayjs;
